import { parseServerDate } from './server-date';

// Biểu tượng và nhãn dùng chung cho hiệu ứng tương tác (cài đặt, trang người xem, overlay).

export const EFFECT_ICONS: Record<string, string> = {
  fireworks: '🎆',
  emoji_rain: '😂',
  sticker_rain: '✨',
  shout_name: '📢',
  strobe: '🚨',
  fog: '🌫️',
  bomb: '💣',
  jumpscare: '👻',
  quake: '🌋',
  upside_down: '🙃',
  mirror: '🪞',
  spin: '🌀',
  drunk: '🥴',
  shrink: '🔍',
  zoom: '🔎',
  stretch: '🫠',
  rainbow: '🌈',
  grayscale: '🖤',
  blackout: '🔌',
  mute_mic: '🤐',
  penalty_scene: '🚫',
};

export const formatCa = (v: number) =>
  `${Number(v || 0).toLocaleString('vi-VN')} Ca`;

/**
 * Làm tròn lên theo phút rồi mới tách giờ: 5400 giây thành "1 giờ 30 phút", 7199 thành "2 giờ", 90 thành "2 phút", 0 thành "0 phút".
 */
export const formatSecondsLeft = (seconds: number) => {
  const s = Math.max(0, Math.floor(seconds || 0));
  const totalMin = Math.ceil(s / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return m > 0 ? `${h} giờ ${m} phút` : `${h} giờ`;
  return `${m} phút`;
};

/** Giờ:phút theo giờ máy người xem, ví dụ "09:05". Chuỗi ngày lỗi thì trả rỗng. */
// Máy chủ trả giờ UTC không kèm múi giờ ("2026-10-19T06:24:00"): đọc qua parseServerDate để hiện đúng giờ máy người xem.
export const formatClock = (iso: string) => {
  const d = parseServerDate(iso);
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Đếm ngược thời gian chờ: 84 thành "1:24". */
export const formatCountdown = (seconds: number) => {
  const s = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export const TRIGGER_STATUS_LABEL: Record<string, string> = {
  Charging: 'Đang trừ tiền',
  Queued: 'Đang chờ chạy',
  Played: 'Đã chạy',
  Missed: 'Không chạy được',
  Failed: 'Lỗi, không trừ tiền',
};

/** Nhãn trạng thái gói thầu trong lịch sử; Charging và Failed không hiện. */
export const billboardStatusLabel = (
  status: string,
  removedReason?: string | null
) => {
  if (status === 'Active') return 'Đang chạy';
  if (status === 'Finished') return 'Đã hết giờ';
  if (status === 'Removed')
    return removedReason ? `Đã gỡ (${removedReason})` : 'Đã gỡ';
  return status;
};

/** Ghi chú khi trò mạnh không chạy được. */
export const TRIGGER_NOTE_LABEL: Record<string, string> = {
  source_missing: ', không tìm thấy nguồn OBS',
  obs_disconnected: ', OBS mất kết nối',
};

export const POSITION_LABELS: Record<string, string> = {
  'top-left': 'Trên trái',
  'top-center': 'Trên giữa',
  'top-right': 'Trên phải',
  'middle-left': 'Giữa trái',
  'middle-center': 'Chính giữa',
  'middle-right': 'Giữa phải',
  'bottom-left': 'Dưới trái',
  'bottom-center': 'Dưới giữa',
  'bottom-right': 'Dưới phải',
};

/** Thời lượng một trò khi chạy trên overlay: kẹp 3–15 giây như máy chủ (giá trị lạ thì lấy 3). */
export const clampEffectDurationSec = (durationSec: number) =>
  Math.min(15, Math.max(3, Number.isFinite(durationSec) ? durationSec : 3));

const isIntIn = (v: number, lo: number, hi: number) =>
  Number.isInteger(v) && v >= lo && v <= hi;

/**
 * Kiểm cài đặt trước khi gửi lên máy chủ, cùng giới hạn với máy chủ. Trả câu báo lỗi đầu tiên, hoặc null nếu hợp lệ.
 * Ô số để trống được coi là NaN và bị báo như giá trị sai; số lẻ (vd 1,5) bị báo riêng, không tự làm tròn.
 */
export const validateLiveEffectForm = (form: {
  effects: {
    name: string;
    priceCa: number;
    durationSec: number;
    cooldownSec: number;
  }[];
  billboardPackages: { hours: number; priceCa: number }[];
  minPriceCa: number;
  maxPriceCa: number;
}): string | null => {
  const minPrice = Math.max(1, form.minPriceCa || 0);
  const maxPrice = form.maxPriceCa > 0 ? form.maxPriceCa : Infinity;
  const priceError = (price: number, what: string) => {
    if (!Number.isInteger(price) || price < minPrice)
      return `${what} tối thiểu ${formatCa(minPrice)}.`;
    if (price > maxPrice) return `${what} tối đa ${formatCa(maxPrice)}.`;
    return null;
  };
  const numbers = [
    ...form.effects.flatMap((e) => [e.priceCa, e.durationSec, e.cooldownSec]),
    ...form.billboardPackages.flatMap((p) => [p.hours, p.priceCa]),
  ];
  if (numbers.some((v) => Number.isFinite(v) && !Number.isInteger(v)))
    return 'Giá, thời lượng, thời gian chờ và số giờ phải là số nguyên.';
  for (let i = 0; i < form.effects.length; i += 1) {
    const e = form.effects[i];
    if (!isIntIn(e.durationSec, 3, 15))
      return `Thời lượng trò ${e.name} phải từ 3 đến 15 giây.`;
    if (!isIntIn(e.cooldownSec, 0, 3600))
      return `Thời gian chờ trò ${e.name} phải từ 0 đến 3600 giây.`;
    const err = priceError(e.priceCa, `Giá trò ${e.name}`);
    if (err) return err;
  }
  const hours = form.billboardPackages.map((p) => p.hours);
  const badHours = hours.findIndex((h) => !isIntIn(h, 1, 24));
  if (badHours >= 0)
    return `Gói thầu thứ ${badHours + 1} phải từ 1 đến 24 giờ.`;
  if (new Set(hours).size !== hours.length)
    return 'Hai gói thầu không được trùng số giờ.';
  for (let i = 0; i < form.billboardPackages.length; i += 1) {
    const p = form.billboardPackages[i];
    const err = priceError(p.priceCa, `Giá gói thầu ${p.hours} giờ`);
    if (err) return err;
  }
  return null;
};

/**
 * Giá hội viên thấy và gửi, cùng công thức máy chủ: không phải hội viên giữ nguyên giá;
 * hội viên được giảm theo phần trăm (kẹp 0 đến 100, làm tròn xuống) nhưng không thấp hơn mức Ca tối thiểu.
 */
export const memberEffectPrice = (
  price: number,
  isMember: boolean,
  discountPercent: number,
  minCa: number
) => {
  if (!isMember) return price;
  const pct = Math.min(100, Math.max(0, discountPercent || 0));
  return Math.max(Math.floor((price * (100 - pct)) / 100), minCa);
};
